const HAS_HTML_TAG = /<[a-z][\s\S]*>/i
const TOKEN = (i) => `\u0000MD${i}\u0000`

const escapeHtml = (value) =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')

const sanitizeHref = (href) => {
    const raw = String(href || '').trim()
    if (!raw) return ''
    if (/^(javascript|vbscript):/i.test(raw)) return ''
    if (/^data:/i.test(raw) && !/^data:image\//i.test(raw)) return ''
    return raw
}

const stripHtmlToText = (html) =>
    String(html ?? '')
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/\n{3,}/g, '\n\n')
        .trim()

const restoreTokens = (text, tokens) =>
    String(text).replace(/\u0000MD(\d+)\u0000/g, (_, n) => tokens[Number(n)] ?? '')

const applyInline = (raw) => {
    const tokens = []
    const stash = (html) => {
        const token = TOKEN(tokens.length)
        tokens.push(html)
        return token
    }

    let text = String(raw ?? '')

    text = text.replace(/`([^`\n]+)`/g, (_, code) =>
        stash(`<code>${escapeHtml(code)}</code>`)
    )

    text = text.replace(
        /!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/g,
        (_, alt, src) => {
            const href = sanitizeHref(src)
            if (!href) return alt
            return stash(
                `<img src="${escapeHtml(href)}" alt="${escapeHtml(alt)}">`
            )
        }
    )

    text = text.replace(
        /\[([^\]]+)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/g,
        (_, label, src) => {
            const href = sanitizeHref(src)
            if (!href) return label
            return stash(
                `<a href="${escapeHtml(href)}" rel="noopener noreferrer">${escapeHtml(label)}</a>`
            )
        }
    )

    text = text.replace(/(^|[\s(])((https?:\/\/[^\s<]+))/g, (_, prefix, url) => {
        const trimmed = url.replace(/[),.;!?]+$/g, '')
        const trailing = url.slice(trimmed.length)
        const href = sanitizeHref(trimmed)
        if (!href) return `${prefix}${url}`
        return `${prefix}${stash(
            `<a href="${escapeHtml(href)}" rel="noopener noreferrer">${escapeHtml(trimmed)}</a>`
        )}${trailing}`
    })

    text = escapeHtml(text)
    text = text.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    text = text.replace(/___(.+?)___/g, '<strong><em>$1</em></strong>')
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    text = text.replace(/__(.+?)__/g, '<strong>$1</strong>')
    text = text.replace(/~~(.+?)~~/g, '<s>$1</s>')
    text = text.replace(/(^|[^*])\*(?!\s)([^*]+?)\*(?!\*)/g, '$1<em>$2</em>')
    text = text.replace(
        /(^|[\s(])_(?!_)([^_\s](?:[^_]*[^_\s])?)_(?!_)/g,
        '$1<em>$2</em>'
    )

    return restoreTokens(text, tokens)
}

const isUnordered = (line) => /^\s*[-*+]\s+\S/.test(line)
const isOrdered = (line) => /^\s*\d+\.\s+\S/.test(line)
const isQuote = (line) => /^\s*>\s?/.test(line)
const isFenceToken = (line) => /^\u0000MD\d+\u0000$/.test(line.trim())

const takeList = (lines, start, match, wrapTag) => {
    const items = []
    let i = start
    while (i < lines.length && match(lines[i])) {
        const body = lines[i]
            .replace(/^\s*[-*+]\s+(?:\[[ xX]\]\s+)?/, '')
            .replace(/^\s*\d+\.\s+/, '')
        items.push(`<li>${applyInline(body)}</li>`)
        i += 1
    }
    return { html: `<${wrapTag}>${items.join('')}</${wrapTag}>`, next: i }
}

/** True when plain text uses markdown that this editor can turn into HTML. */
export const looksLikeMarkdown = (value) => {
    const src = String(value ?? '')
        .replace(/\r\n/g, '\n')
        .trim()
    if (!src || HAS_HTML_TAG.test(src)) return false

    return (
        /^#{1,6}\s+\S/m.test(src) ||
        /^```/m.test(src) ||
        /^\s*>\s+\S/m.test(src) ||
        /^\s*[-*+]\s+\S/m.test(src) ||
        /^\s*\d+\.\s+\S/m.test(src) ||
        /^\s*([-*_]){3,}\s*$/m.test(src) ||
        /\[[^\]]+\]\([^)\s]+\)/.test(src) ||
        /!\[[^\]]*\]\([^)\s]+\)/.test(src) ||
        /\*\*[^*]+?\*\*/.test(src) ||
        /__[^_]+?__/.test(src) ||
        /~~[^~]+?~~/.test(src) ||
        /`[^`\n]+`/.test(src) ||
        /(^|[^*])\*(?!\s)([^*]+?)\*(?!\*)/.test(src)
    )
}

/**
 * Convert clipboard markdown only when the HTML side is still source markdown
 * (e.g. VS Code), not already-rendered rich text (e.g. GitHub preview).
 */
export const shouldConvertPastedMarkdown = (plain, html) => {
    if (!looksLikeMarkdown(plain)) return false
    if (!String(html || '').trim()) return true
    return looksLikeMarkdown(stripHtmlToText(html))
}

/** Convert a markdown string to HTML Quill can paste. Raw HTML is escaped. */
export const markdownToHtml = (markdown) => {
    const src = String(markdown ?? '').replace(/\r\n/g, '\n')
    if (!src.trim()) return ''

    const tokens = []
    const stash = (html) => {
        const token = TOKEN(tokens.length)
        tokens.push(html)
        return token
    }

    let body = src.replace(/```[^\n]*\n?([\s\S]*?)```/g, (_, code) => {
        const lines = String(code)
            .replace(/\n$/, '')
            .split('\n')
            .map((line) => escapeHtml(line))
        return `\n${stash(`<pre class="ql-syntax">${lines.join('\n')}</pre>`)}\n`
    })

    const lines = body.split('\n')
    const out = []
    let i = 0

    while (i < lines.length) {
        const line = lines[i]
        if (!line.trim()) {
            i += 1
            continue
        }

        if (isFenceToken(line.trim())) {
            out.push(line.trim())
            i += 1
            continue
        }

        if (/^\s*([-*_]){3,}\s*$/.test(line)) {
            out.push('<hr>')
            i += 1
            continue
        }

        const heading = line.match(/^(#{1,6})\s+(.+)$/)
        if (heading) {
            const level = Math.min(heading[1].length, 6)
            out.push(`<h${level}>${applyInline(heading[2])}</h${level}>`)
            i += 1
            continue
        }

        if (isQuote(line)) {
            const quote = []
            while (i < lines.length && isQuote(lines[i])) {
                quote.push(lines[i].replace(/^\s*>\s?/, ''))
                i += 1
            }
            const inner = quote
                .filter((row) => row.trim())
                .map((row) => `<p>${applyInline(row)}</p>`)
                .join('')
            out.push(`<blockquote>${inner || '<p><br></p>'}</blockquote>`)
            continue
        }

        if (isUnordered(line)) {
            const taken = takeList(lines, i, isUnordered, 'ul')
            out.push(taken.html)
            i = taken.next
            continue
        }

        if (isOrdered(line)) {
            const taken = takeList(lines, i, isOrdered, 'ol')
            out.push(taken.html)
            i = taken.next
            continue
        }

        const para = []
        while (
            i < lines.length &&
            lines[i].trim() &&
            !isFenceToken(lines[i].trim()) &&
            !/^#{1,6}\s+\S/.test(lines[i]) &&
            !isQuote(lines[i]) &&
            !isUnordered(lines[i]) &&
            !isOrdered(lines[i]) &&
            !/^\s*([-*_]){3,}\s*$/.test(lines[i])
        ) {
            para.push(lines[i])
            i += 1
        }
        out.push(`<p>${para.map((row) => applyInline(row)).join('<br>')}</p>`)
    }

    return restoreTokens(out.join(''), tokens)
}
